import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { User, Briefcase, Calendar, ExternalLink, CheckCircle, XCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Application {
  id: string;
  status: string;
  applied_at: string;
  candidate_id: string;
  job_posting: {
    id: string;
    title: string;
  };
  candidate: {
    id: string;
    full_name: string;
    email: string;
  };
}

export default function ApplicationsReceived() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // First, get employer's job posting IDs
      const { data: employerJobs } = await supabase
        .from("job_postings")
        .select("id")
        .eq("employer_id", user.id);

      const jobIds = employerJobs?.map(j => j.id) || [];

      if (jobIds.length === 0) {
        setApplications([]);
        return;
      }

      // Then get applications for those jobs with details
      const { data, error } = await supabase
        .from("job_applications")
        .select(`
          *,
          job_posting:job_postings!job_posting_id (
            id,
            title
          )
        `)
        .in("job_posting_id", jobIds)
        .order("applied_at", { ascending: false });

      if (error) throw error;

      setApplications((data as any) || []);
    } catch (error: any) {
      console.error("Fetch error:", error);
      toast({
        title: "Error Loading Applications",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (applicationId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from("job_applications")
        .update({
          status: newStatus,
          last_status_change: new Date().toISOString()
        })
        .eq("id", applicationId);

      if (error) throw error;

      toast({
        title: "Status Updated",
        description: `Application status changed to ${newStatus}.`,
      });

      fetchApplications();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "reviewed":
        return "bg-blue-100 text-blue-800";
      case "interview":
        return "bg-purple-100 text-purple-800";
      case "offered":
        return "bg-green-100 text-green-800";
      case "accepted":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const filteredApplications = applications.filter((app) => {
    if (filter === "all") return true;
    return app.status === filter;
  });

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 bg-muted animate-pulse rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Applications Received</CardTitle>
              <CardDescription>Review and manage candidate applications</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-6 flex-wrap">
            <Button
              variant={filter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("all")}
            >
              All ({applications.length})
            </Button>
            <Button
              variant={filter === "pending" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("pending")}
            >
              Pending ({applications.filter(a => a.status === "pending").length})
            </Button>
            <Button
              variant={filter === "reviewed" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("reviewed")}
            >
              Reviewed ({applications.filter(a => a.status === "reviewed").length})
            </Button>
            <Button
              variant={filter === "interview" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("interview")}
            >
              Interview ({applications.filter(a => a.status === "interview").length})
            </Button>
          </div>

          {filteredApplications.length === 0 ? (
            <div className="text-center py-12">
              <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No applications yet</h3>
              <p className="text-muted-foreground">
                {filter === "all"
                  ? "Applications will appear here once candidates apply to your jobs."
                  : `No ${filter} applications at the moment.`}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredApplications.map((application) => (
                <Card key={application.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold">
                            {application.candidate?.full_name || "Anonymous"}
                          </h3>
                          <Badge className={getStatusColor(application.status)}>
                            {application.status}
                          </Badge>
                        </div>

                        <div className="space-y-2 text-sm text-muted-foreground mb-3">
                          <p className="flex items-center gap-1">
                            <Briefcase className="h-4 w-4" />
                            Applied for: <span className="font-medium text-foreground">{application.job_posting?.title || "Unknown Position"}</span>
                          </p>
                          <p className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Applied: {new Date(application.applied_at).toLocaleDateString()}
                          </p>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/profile/${application.candidate_id}`)}
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            View Profile
                          </Button>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 ml-4">
                        <Select
                          value={application.status}
                          onValueChange={(value) => updateApplicationStatus(application.id, value)}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="reviewed">Reviewed</SelectItem>
                            <SelectItem value="interview">Interview</SelectItem>
                            <SelectItem value="offered">Offered</SelectItem>
                            <SelectItem value="accepted">Accepted</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
